import { useTranslation } from 'react-i18next';

import type { ChatAgent, ChatDiscountOptions } from '@nucleus-commerce/api-client/admin';

import type { ActionForm } from '../schemas/trigger-schema';

interface TriggerPreviewProps {
  actions: readonly ActionForm[];
  storeName: string;
  agents: ChatAgent[] | null;
  discounts: ChatDiscountOptions | undefined;
  suggestedRepliesEnabled: boolean;
}

/** Roughly what the shopper sees, step by step; tags never reach the shopper and say so. */
export function TriggerPreview({
  actions,
  storeName,
  agents,
  discounts,
  suggestedRepliesEnabled,
}: TriggerPreviewProps) {
  const { t } = useTranslation('chatSettings');
  const sender = (action: ActionForm) => {
    if (action.sendAs === 'alias' && action.alias.trim()) return action.alias.trim();
    if (action.sendAs === 'member') {
      const agent = agents?.find((item) => item.userId === action.asUserId);
      if (agent) return agent.chatDisplayName ?? agent.name.split(/\s+/)[0] ?? agent.name;
    }
    return storeName;
  };
  return (
    <ol
      aria-label={t('triggers.preview')}
      className="bg-muted/40 space-y-2 rounded-md border p-3 text-sm"
    >
      {actions.map((action, index) => {
        const key = `${String(index)}-${action.type}`;
        if (action.type === 'message') {
          return (
            <li key={key} className="space-y-1">
              <span className="text-muted-foreground text-xs">{sender(action)}</span>
              <p className="bg-background max-w-[85%] rounded-2xl rounded-bl-sm border px-3 py-2 break-words whitespace-pre-wrap">
                {action.body.trim() || t('triggers.previewEmpty')}
              </p>
              {suggestedRepliesEnabled && action.suggestedReplies.length > 0 ? (
                <p className="flex flex-wrap gap-1.5">
                  {action.suggestedReplies.map((reply) => (
                    <span key={reply} className="rounded-full border px-2 py-0.5 text-xs">
                      {reply}
                    </span>
                  ))}
                </p>
              ) : null}
            </li>
          );
        }
        const note =
          action.type === 'wait'
            ? t('triggers.previewWait', { seconds: action.seconds || '?' })
            : action.type === 'tag'
              ? t('triggers.previewTag', { tag: action.tag || '?' })
              : action.type === 'product_card'
                ? t('triggers.previewProduct', {
                    title: action.productTitle || t('triggers.noProduct'),
                  })
                : t('triggers.previewDiscount', {
                    title:
                      discounts?.items.find((item) => item.id === action.discountId)?.title ?? '?',
                  });
        return (
          <li key={key} className="text-muted-foreground text-xs italic">
            {note}
          </li>
        );
      })}
    </ol>
  );
}
